"use client";

import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/Select";
import { Product, CustomInput } from "@/types/product";
import { formatCurrency } from "@/lib/utils/general";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { useSession } from "next-auth/react";
import { toast } from "sonner";
import { logger } from "@/lib/utils/logger";

interface ProductFormProps {
	product: Product;
	customInputs: CustomInput[];
}

export default function ProductForm({
	product,
	customInputs,
}: ProductFormProps) {
	const router = useRouter();
	const session = useSession();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [formData, setFormData] = useState<Record<string, string>>({});
	const [errors, setErrors] = useState<Record<string, boolean>>({});
	const [serverError, setServerError] = useState<string | string[] | null>(
		null
	);
	const [showConfirmation, setShowConfirmation] = useState(false);
	const [showInstantDeliveryModal, setShowInstantDeliveryModal] =
		useState(false);
	const [instantDeliveryCode, setInstantDeliveryCode] = useState<{
		code: string;
		description?: string;
	} | null>(null);
	const [orderId, setOrderId] = useState<string | null>(null);

	const isOutOfStock = product.stock <= 0;

	const handleInputChange = (label: string, value: string) => {
		setFormData((prev) => ({ ...prev, [label]: value }));
		// Clear error when user types
		if (errors[label]) {
			setErrors((prev) => ({ ...prev, [label]: false }));
		}
	};

	const validateForm = () => {
		const newErrors: Record<string, boolean> = {};
		let isValid = true;

		customInputs.forEach((input) => {
			if (
				input.required &&
				(!formData[input.label] || formData[input.label].trim() === "")
			) {
				newErrors[input.label] = true;
				isValid = false;
			}
		});

		setErrors(newErrors);
		return isValid;
	};

	const handleBuyNow = () => {
		// Check if user is logged in
		if (!session) {
			toast.warning("Login Required", {
				description: "Please sign in to purchase this product.",
			});
			router.push(`/auth?callbackUrl=/products/${product.id}`);
			return;
		}

		if (customInputs.length === 0 || validateForm()) {
			setShowConfirmation(true);
		}
	};

	const handleConfirmPurchase = async () => {
		setIsSubmitting(true);
		setShowConfirmation(false);

		try {
			// Create order with product and custom inputs
			const response = await fetch("/api/orders/create", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					productId: product.id,
					customInputs: formData,
					quantity: 1,
					totalAmount: product.hasDiscount
						? product.discountedPrice || 0
						: product.price,
				}),
			});

			const data = await response.json();

			if (!response.ok) {
				if (response.status === 401) {
					toast.error("Authentication Required", {
						description: "Please sign in to complete your purchase.",
					});
					router.push(`/auth?callbackUrl=/products/${product.id}`);
					setIsSubmitting(false);
					return;
				}
				if (data.needBalance && data.needBalance > 0) {
					logger.warn("Insufficient balance");
					setServerError([
						`${data.error}. You need ${formatCurrency(
							data.needBalance
						)} more to purchase this product.`,
						"/dashboard/balance?balance=" + data.needBalance,
					]);
					setIsSubmitting(false);
					return;
				} else {
					throw new Error(data.error || "Failed to create order");
				}
			}

			// Store the order ID
			setOrderId(data.orderId);

			// Check if this is an instant delivery product with a code
			logger.info("Instant delivery code received:", data.instantDeliveryCode);
			if (product.isInstantDelivery && data.instantDeliveryCode) {
				// Show the instant delivery code in a modal or alert
				setInstantDeliveryCode(data.instantDeliveryCode);
				setShowInstantDeliveryModal(true);
				setIsSubmitting(false);
				return;
			} else {
				// Find the chat with the order id
				const chatResponse = await fetch(
					`/api/chat/findByOrder/${data.orderId}`
				);

				if (chatResponse.ok) {
					const chatData = await chatResponse.json();
					// Redirect to chat page with the chat id
					router.push(`/dashboard/chat/${chatData.chatId}`);
				} else {
					router.push(`/dashboard/inventory/${data.orderId}`);
				}
			}
		} catch (error) {
			logger.error("Error creating order:", error);
			setIsSubmitting(false);
			setServerError(
				error instanceof Error
					? error.message
					: (error as { error?: string })?.error || "An unknown error occurred"
			);
		}
	};

	return (
		<div className="max-w-3xl mx-auto w-full">
			{/* Product Header */}
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.5 }}
				className="space-y-6 mb-8"
			>
				{/* Product Title */}
				<motion.div
					className="group relative overflow-hidden rounded-xl border border-gray-800 bg-gray-900 p-6 shadow-lg transition-all duration-300 hover:border-gray-700 hover:shadow-xl"
					whileHover={{ scale: 1.01 }}
				>
					<div className="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-primary/20 opacity-20 transition-all duration-300 group-hover:scale-150"></div>
					<h1 className="text-3xl font-bold text-gray-100">{product.name}</h1>
				</motion.div>

				{/* Product Description */}
				{product.shortDescription && (
					<motion.div
						className="group relative overflow-hidden rounded-xl border border-gray-800 bg-gray-900/50 p-6 shadow-lg transition-all duration-300 hover:border-gray-700 hover:shadow-xl"
						whileHover={{ scale: 1.01 }}
					>
						<div className="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-secondary/20 opacity-20 transition-all duration-300 group-hover:scale-150"></div>
						<div className="prose prose-lg max-w-none text-gray-300">
							<div className="whitespace-pre-wrap break-words">
								{product.shortDescription}
							</div>
						</div>
					</motion.div>
				)}
			</motion.div>

			{/* Price Card */}
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.5, delay: 0.1 }}
				className="group relative overflow-hidden rounded-xl border border-gray-800 bg-gray-900 p-8 shadow-lg transition-all duration-300 hover:border-gray-700 hover:shadow-xl mb-8"
				whileHover={{ scale: 1.01 }}
			>
				<div className="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-accent/20 opacity-20 transition-all duration-300 group-hover:scale-150"></div>
				<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
					<div className="flex items-baseline gap-3">
						{product.hasDiscount && (
							<span className="text-xl font-bold text-red-400 line-through">
								{formatCurrency(product.price)}
							</span>
						)}
						<span className="text-4xl font-bold text-gray-100">
							{formatCurrency(
								product.hasDiscount
									? product.discountedPrice || 0
									: product.price
							)}
						</span>
					</div>

					{isOutOfStock ? (
						<div className="inline-flex items-center px-4 py-2 bg-red-500/10 text-red-400 rounded-lg text-sm font-medium">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								className="h-5 w-5 mr-2"
								viewBox="0 0 20 20"
								fill="currentColor"
							>
								<path
									fillRule="evenodd"
									d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
									clipRule="evenodd"
								/>
							</svg>
							Out of Stock
						</div>
					) : (
						product.showLowStockWarning &&
						product.stock <= (product.lowStockThreshold || 5) && (
							<div className="inline-flex items-center px-4 py-2 bg-yellow-500/10 text-yellow-400 rounded-lg text-sm font-medium">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									className="h-5 w-5 mr-2"
									viewBox="0 0 20 20"
									fill="currentColor"
								>
									<path
										fillRule="evenodd"
										d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
										clipRule="evenodd"
									/>
								</svg>
								Only {product.stock} left in stock
							</div>
						)
					)}
				</div>
			</motion.div>

			{/* Custom Inputs Section */}
			{product.requireCustomInputs && customInputs.length > 0 && (
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.5, delay: 0.2 }}
					className="mb-8"
				>
					<h2 className="text-xl font-semibold mb-4 text-gray-100 flex items-center gap-2">
						<svg
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 24 24"
							strokeWidth={1.5}
							stroke="currentColor"
							className="w-6 h-6 text-primary"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z"
							/>
						</svg>
						Additional Information
					</h2>
					<div className="group relative overflow-hidden rounded-xl border border-gray-800 bg-gray-900 p-8 shadow-lg transition-all duration-300 hover:border-gray-700 hover:shadow-xl">
						<div className="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-primary/20 opacity-20 transition-all duration-300 group-hover:scale-150"></div>
						<div className="space-y-6">
							{customInputs.map((input, index) => (
								<div key={index} className="space-y-2">
									<Label className="text-gray-300 font-medium flex items-center">
										{input.label}
										{input.required && (
											<span className="text-red-400 ml-1">*</span>
										)}
									</Label>
									{input.type === "text" && (
										<Input
											type="text"
											placeholder={`Enter ${input.label.toLowerCase()}`}
											required={input.required}
											disabled={isOutOfStock}
											className={`bg-gray-800 border ${
												errors[input.label]
													? "border-red-500"
													: "border-gray-700"
											} focus:border-primary focus:ring-1 focus:ring-primary transition-all duration-200 rounded-lg h-11 text-gray-100 ${
												isOutOfStock ? "opacity-50 cursor-not-allowed" : ""
											}`}
											value={formData[input.label] || ""}
											onChange={(e) =>
												handleInputChange(input.label, e.target.value)
											}
										/>
									)}
									{input.type === "number" && (
										<Input
											type="number"
											placeholder={`Enter ${input.label.toLowerCase()}`}
											required={input.required}
											disabled={isOutOfStock}
											className={`bg-gray-800 border ${
												errors[input.label]
													? "border-red-500"
													: "border-gray-700"
											} focus:border-primary focus:ring-1 focus:ring-primary transition-all duration-200 rounded-lg h-11 text-gray-100 ${
												isOutOfStock ? "opacity-50 cursor-not-allowed" : ""
											}`}
											value={formData[input.label] || ""}
											onChange={(e) =>
												handleInputChange(input.label, e.target.value)
											}
										/>
									)}
									{input.type === "select" && (
										<Select
											value={formData[input.label] || ""}
											onValueChange={(value) =>
												handleInputChange(input.label, value)
											}
											disabled={isOutOfStock}
										>
											<SelectTrigger
												className={`bg-gray-800 border ${
													errors[input.label]
														? "border-red-500"
														: "border-gray-700"
												} focus:border-primary focus:ring-1 focus:ring-primary transition-all duration-200 rounded-lg h-11 text-gray-100 ${
													isOutOfStock ? "opacity-50 cursor-not-allowed" : ""
												}`}
											>
												<SelectValue
													placeholder={`Select ${input.label.toLowerCase()}`}
												/>
											</SelectTrigger>
											<SelectContent>
												{input.options?.map((option, optionIndex) => (
													<SelectItem key={optionIndex} value={option}>
														{option}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									)}
									{errors[input.label] && (
										<p className="text-red-400 text-sm mt-1 flex items-center">
											<svg
												xmlns="http://www.w3.org/2000/svg"
												className="h-4 w-4 mr-1"
												viewBox="0 0 20 20"
												fill="currentColor"
											>
												<path
													fillRule="evenodd"
													d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
													clipRule="evenodd"
												/>
											</svg>
											This field is required
										</p>
									)}
								</div>
							))}
						</div>
					</div>
				</motion.div>
			)}

			{serverError && (
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.3 }}
					className="flex items-center gap-2 p-4 text-sm font-medium text-red-400 bg-red-500/10 rounded-lg border border-red-500/20 mb-8"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						className="h-5 w-5"
						viewBox="0 0 20 20"
						fill="currentColor"
					>
						<path
							fillRule="evenodd"
							d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
							clipRule="evenodd"
						/>
					</svg>
					{Array.isArray(serverError) ? (
						<Link href={serverError[1]} className="hover:underline">
							{serverError[0]}
						</Link>
					) : (
						serverError
					)}
				</motion.div>
			)}

			{/* Buy Now Button */}
			{!isOutOfStock && (
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.5, delay: 0.3 }}
					className="pt-4"
				>
					<Button
						className="w-full h-12 text-base font-medium bg-primary hover:bg-primary-focus text-primary-content transition-all duration-300 rounded-lg shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
						onClick={handleBuyNow}
						disabled={isSubmitting}
					>
						{isSubmitting ? (
							<div className="flex items-center justify-center">
								<svg
									className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
								>
									<circle
										className="opacity-25"
										cx="12"
										cy="12"
										r="10"
										stroke="currentColor"
										strokeWidth="4"
									></circle>
									<path
										className="opacity-75"
										fill="currentColor"
										d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
									></path>
								</svg>
								Processing...
							</div>
						) : (
							<div className="flex items-center justify-center">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									className="h-5 w-5 mr-2"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
									/>
								</svg>
								Buy Now
							</div>
						)}
					</Button>
				</motion.div>
			)}

			{/* Confirmation Modal */}
			<AnimatePresence>
				{showConfirmation && (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
					>
						<motion.div
							initial={{ scale: 0.95, opacity: 0 }}
							animate={{ scale: 1, opacity: 1 }}
							exit={{ scale: 0.95, opacity: 0 }}
							transition={{ duration: 0.2 }}
							className="bg-gray-900 rounded-xl shadow-2xl max-w-md w-full p-8 border border-gray-800"
						>
							{/* Header */}
							<div className="flex items-center justify-between mb-6">
								<h3 className="text-2xl font-bold text-gray-100">
									Confirm Purchase
								</h3>
								<button
									onClick={() => setShowConfirmation(false)}
									className="text-gray-400 hover:text-gray-300 transition-colors"
								>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										className="h-6 w-6"
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M6 18L18 6M6 6l12 12"
										/>
									</svg>
								</button>
							</div>

							{/* Content */}
							<div className="space-y-6">
								<div className="bg-gray-800/50 p-5 rounded-lg border border-gray-700">
									<p className="text-gray-300">
										Are you sure you want to purchase{" "}
										<span className="font-semibold text-gray-100">
											{product.name}
										</span>{" "}
										for{" "}
										<span className="font-semibold text-primary">
											{formatCurrency(
												product.hasDiscount
													? product.discountedPrice || 0
													: product.price
											)}
										</span>
										?
									</p>
								</div>

								{product.requireCustomInputs && customInputs.length > 0 && (
									<div className="bg-gray-800 p-5 rounded-lg border border-gray-700">
										<h4 className="text-sm font-semibold mb-3 text-gray-300">
											Order Details
										</h4>
										<ul className="space-y-0 divide-y divide-gray-700">
											{Object.entries(formData).map(([key, value], index) => (
												<li
													key={key}
													className={`flex justify-between items-center py-2 ${
														index === 0 ? "pt-0" : ""
													} ${
														index === Object.entries(formData).length - 1
															? "pb-0"
															: ""
													}`}
												>
													<span className="text-gray-400 capitalize">
														{key}
													</span>
													<span className="font-medium text-gray-200">
														{value}
													</span>
												</li>
											))}
										</ul>
									</div>
								)}

								<div className="bg-gray-800/30 p-4 rounded-lg">
									<p className="text-sm text-gray-400">
										By confirming, you agree to our{" "}
										<Link
											href="/policies/terms"
											className="text-primary hover:text-primary-focus transition-colors hover:underline"
											target="_blank"
											rel="noopener noreferrer"
										>
											Terms of Service
										</Link>{" "}
										and{" "}
										<Link
											href="/policies/returns"
											className="text-primary hover:text-primary-focus transition-colors hover:underline"
											target="_blank"
											rel="noopener noreferrer"
										>
											Return Policy
										</Link>
										.
									</p>
								</div>
							</div>

							{/* Actions */}
							<div className="flex gap-4 mt-8">
								<Button
									className="flex-1 bg-gray-800 hover:bg-gray-700 text-gray-300 transition-colors cursor-pointer"
									onClick={() => setShowConfirmation(false)}
								>
									Cancel
								</Button>
								<Button
									className="flex-1 bg-primary hover:bg-primary-focus text-primary-content transition-colors cursor-pointer"
									onClick={handleConfirmPurchase}
								>
									Confirm Purchase
								</Button>
							</div>
						</motion.div>
					</motion.div>
				)}
			</AnimatePresence>

			{/* Instant Delivery Modal */}
			<AnimatePresence>
				{showInstantDeliveryModal && instantDeliveryCode && (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
					>
						<motion.div
							initial={{ scale: 0.95, opacity: 0 }}
							animate={{ scale: 1, opacity: 1 }}
							exit={{ scale: 0.95, opacity: 0 }}
							transition={{ duration: 0.2 }}
							className="w-full max-w-3xl p-8 space-y-6 overflow-hidden rounded-xl bg-gray-900 shadow-2xl border border-gray-800"
						>
							<div className="space-y-3">
								<div className="flex items-center gap-3">
									<div className="p-2 rounded-full bg-primary/10">
										<svg
											className="w-6 h-6 text-primary"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={2}
												d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
											/>
										</svg>
									</div>
									<h3 className="text-2xl font-bold text-gray-100">
										Instant Delivery Code
									</h3>
								</div>
								<p className="text-gray-300 text-lg">
									Your product has been delivered instantly! Here is your code:
								</p>
							</div>

							<div className="p-6 space-y-4 rounded-xl bg-gray-800/50 border border-gray-700">
								<div className="flex items-center justify-between p-4 rounded-lg bg-gray-800">
									<span className="font-medium text-gray-300">Code:</span>
									<span className="font-mono text-lg font-bold text-primary tracking-wider text-right">
										{instantDeliveryCode.code}
									</span>
								</div>
								{instantDeliveryCode.description && (
									<div className="p-4 rounded-lg bg-gray-800">
										<span className="block font-medium text-gray-300 mb-2">
											Description:
										</span>
										<p className="text-base text-gray-400 leading-relaxed">
											{instantDeliveryCode.description}
										</p>
									</div>
								)}
							</div>

							<div className="flex justify-end pt-4">
								{orderId && (
									<Link
										href={`/dashboard/inventory/${orderId}`}
										className="bg-primary text-white hover:bg-primary-focus px-6 py-2.5 rounded-lg transition-all duration-200 hover:scale-105 mr-4"
									>
										Show in Inventory
									</Link>
								)}

								<Button
									onClick={() => setShowInstantDeliveryModal(false)}
									className="bg-primary text-white hover:bg-primary-focus px-6 py-2.5 rounded-lg transition-all duration-200 hover:scale-105"
								>
									Close
								</Button>
							</div>
						</motion.div>
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
}
